import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils"

/**
 * Focus is unified across Button and Badge: a two-pixel emerald ring, offset
 * against the page canvas. `focus-visible` rather than `focus`, so a mouse
 * click does not leave a ring behind.
 */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-btn text-sm font-medium",
    "transition-colors duration-control ease-editorial",
    FOCUS_RING,
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",

        /*
         * Editorial Analytics variants. These map onto the token palette rather
         * than the shadcn HSL variables, so they stay in step with the rest of
         * the design system.
         */
        primary:
          "bg-emerald-700 text-white hover:bg-emerald-800 active:bg-emerald-800",
        quiet:
          "border border-line-strong bg-white text-navy-950 hover:bg-offwhite hover:border-navy-800/30",
        danger:
          "bg-red-600 text-white hover:bg-red-700 active:bg-red-700",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-btn px-3 text-xs",
        lg: "h-10 rounded-btn px-8",
        icon: "h-9 w-9",

        /** Primary call to action. 48px, per the spacing scale. */
        cta: "h-cta px-6 text-base font-semibold",
        /**
         * Standard compact control. 44px where a finger is the input device,
         * 40px from `lg` up. See `.tap-safe` in `src/index.css`.
         */
        control: "tap-safe px-4 text-sm font-semibold",
        /** Icon button that still clears the 44px mobile touch target. */
        "icon-touch": "h-touch w-touch",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * @param {boolean} [loading] Renders a spinner in place of any leading icon and
 *   marks the control busy for assistive technology. The button is disabled
 *   while loading, so a second submit cannot fire.
 */
const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // `asChild` hands rendering to the caller's element, so injecting a spinner
    // would give Slot two children and throw. Loading state is the caller's
    // responsibility in that case.
    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
        {children}
      </Comp>
    );
  }
)
Button.displayName = "Button"

export { Button, buttonVariants, FOCUS_RING }
